import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ShareStoryDialog from "@/components/support/ShareStoryDialog";
import Wordmark from "@/components/Wordmark";
import waveLogo from "@/assets/jersey-shore-wave-logo.png";
import heroHuddle from "@/assets/jsw-1301.jpg.asset.json";
import { athleteImageUrl } from "@/lib/athleteImage";
import { toExcerpt, toPlainText } from "@/lib/text";
import { trackEvent } from "@/lib/analytics";
import { buildSubscribeUrl, isValidEmail } from "@/lib/newsletter";

const TEAM_SLUG = "jersy-shore-wave";
const CAMPAIGN = "know-her-name";

type RosterAthlete = {
  id: string;
  slug: string;
  name: string;
  sport: string;
  country: string | null;
  tagline: string | null;
  bio: string | null;
  image_path: string | null;
};

const steps = [
  {
    title: "Meet her",
    body: "Read who she is, what she plays for, and what she is building beyond the roster.",
  },
  {
    title: "Share her story",
    body: "Send her profile to one person. Visibility is the first thing every athlete asks for.",
  },
  {
    title: "Back her game",
    body: "Follow her, support her directly, or bring her a real opportunity.",
  },
];

const Wave = () => {
  const [roster, setRoster] = useState<RosterAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [signedUp, setSignedUp] = useState(false);
  const rosterRef = useRef<HTMLElement | null>(null);
  const rosterSeen = useRef(false);

  useEffect(() => {
    trackEvent("campaign_view", { campaign: CAMPAIGN, page: "/wave" });
  }, []);

  useEffect(() => {
    (async () => {
      const { data: team } = await supabase
        .from("teams")
        .select("id")
        .eq("slug", TEAM_SLUG)
        .eq("published", true)
        .maybeSingle();
      if (!team) {
        setLoading(false);
        return;
      }
      const { data: links } = await supabase
        .from("athlete_teams")
        .select("athlete_id")
        .eq("team_id", (team as { id: string }).id);
      const ids = (links ?? []).map((l: { athlete_id: string }) => l.athlete_id);
      if (ids.length) {
        const { data } = await supabase
          .from("athletes")
          .select("id,slug,name,sport,country,tagline,bio,image_path")
          .in("id", ids)
          .eq("published", true)
          .order("sort_order");
        setRoster((data as RosterAthlete[]) ?? []);
      }
      setLoading(false);
    })();
  }, []);

  // Fire roster_view once the roster actually scrolls into view.
  useEffect(() => {
    const el = rosterRef.current;
    if (!el || loading || rosterSeen.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !rosterSeen.current) {
          rosterSeen.current = true;
          trackEvent("roster_view", { campaign: CAMPAIGN, count: roster.length });
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, roster.length]);

  const onSubmitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError(null);
    setSignedUp(true);
    trackEvent("email_signup_completed", { campaign: CAMPAIGN });
    window.open(buildSubscribeUrl(email), "_blank", "noopener,noreferrer");
  };

  const cards = useMemo(() => roster, [roster]);

  // Approved Wave team-huddle photo for the hero media panel. If the asset
  // is unavailable, fall back to the athlete portrait collage.
  const [heroImgFailed, setHeroImgFailed] = useState(false);

  // Up to three published athletes with real photos for the hero collage fallback.
  const heroPortraits = useMemo(
    () => roster.filter((a) => a.image_path).slice(0, 3),
    [roster],
  );

  // One shared campaign container for every section on the page.
  const wrap = "mx-auto w-full max-w-[1200px] px-6 lg:px-10";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-secondary text-secondary-foreground">
          <div className={`${wrap} py-14 md:py-20`}>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-stretch">
              <div className="lg:col-span-7 flex flex-col justify-center">
                <div className="inline-flex items-center gap-3 sm:gap-4 flex-wrap bg-background rounded-2xl px-4 py-3 shadow-soft">
                  <Wordmark size="sm" />
                  <span aria-hidden className="text-muted-foreground text-xl">×</span>
                  <img
                    src={waveLogo}
                    alt="Jersey Shore Wave"
                    width={160}
                    height={80}
                    className="h-8 sm:h-10 w-auto"
                  />
                </div>

                <p className="mt-8 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-wave">
                  A Say It In Sports campaign with the Jersey Shore Wave
                </p>
                <h1 className="mt-3 text-[clamp(2.25rem,7vw,4.75rem)] leading-[0.95] text-balance">
                  Know her name.
                  <br />
                  <span className="serif font-normal text-primary">Back her game.</span>
                  <sup className="text-[0.35em] align-super ml-1" aria-label="trademark">™</sup>
                </h1>
                <p className="mt-6 max-w-xl text-base sm:text-lg text-secondary-foreground/80">
                  Ten athletes. One roster. Read her story, send it to someone, and back her
                  season in a way she can actually feel.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                  <a
                    href="#roster"
                    className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold shadow-bold hover:bg-wave hover:text-wave-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
                  >
                    Meet the athletes <ArrowRight className="w-4 h-4" aria-hidden />
                  </a>
                  <a
                    href="#how-to-back"
                    className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full border border-secondary-foreground/30 font-semibold hover:bg-secondary-foreground/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
                  >
                    How to back her game
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5 h-full">
                {!heroImgFailed ? (
                  <div className="relative w-full h-full min-h-[320px] sm:min-h-[400px] lg:min-h-full rounded-2xl lg:rounded-3xl overflow-hidden">
                    <img
                      src={heroHuddle.url}
                      alt="Jersey Shore Wave players gather in a game-day huddle."
                      loading="eager"
                      width={1600}
                      height={1067}
                      onError={() => setHeroImgFailed(true)}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  </div>
                ) : heroPortraits.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3 items-end">
                    {heroPortraits.map((a, i) => (
                      <div
                        key={a.id}
                        className={`rounded-2xl overflow-hidden border-4 border-wave shadow-bold bg-muted aspect-[3/4] ${
                          i === 1 ? "mb-8" : ""
                        }`}
                      >
                        <img
                          src={athleteImageUrl(a.image_path)}
                          alt={a.name}
                          loading="eager"
                          width={384}
                          height={512}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-3xl bg-secondary/60 border-4 border-wave shadow-bold px-8 py-10 md:px-10 md:py-14 flex flex-col items-center justify-center text-center">
                    <p className="font-display text-2xl md:text-3xl text-balance">
                      10 athletes. One roster.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Three steps */}
        <section id="how-to-back" className="py-12 md:py-16 bg-muted/40 border-b border-border scroll-mt-20">
          <div className={wrap}>
            <h2 className="text-3xl md:text-4xl text-balance">How to back her game</h2>
            <ol className="grid gap-4 sm:grid-cols-3 mt-8 items-stretch">
              {steps.map((s, i) => (
                <li key={s.title} className="h-full flex flex-col bg-card rounded-2xl px-5 py-4 md:p-5 shadow-card border-t-4 border-wave">
                  <span className="font-display text-2xl text-wave">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl mt-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{s.body}</p>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex justify-center">
              <a
                href="#roster"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-secondary px-6 py-3 font-semibold text-secondary-foreground transition-colors hover:bg-wave hover:text-wave-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Meet the athletes <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>
        </section>

        {/* Why individual support exists */}
        <section id="why-support" className="py-12 md:py-16 bg-background border-b border-border scroll-mt-20">
          <div className={wrap}>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-wave">
                The team behind the stories
              </span>
              <h2 className="text-3xl md:text-4xl mt-2 text-balance">
                The Wave is the team. She is the story.
              </h2>
              <div className="mt-4 space-y-4 text-muted-foreground">
                <p>
                  The Jersey Shore Wave is building something bigger for women’s football: a team, a community and a place for athletes to grow.
                </p>
                <p>
                  But every athlete also brings her own goals, costs and opportunities. Individual support lets fans back a specific athlete whose story they connect with — helping her pursue the season, build her platform and access opportunities beyond the field.
                </p>
                <p>
                  Choose a player. Read her story. Share it. Then back her in a way that feels meaningful to you.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link
                  to="/teams/jersey-shore-wave?from=wave"
                  className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-wave text-wave-foreground font-semibold hover:bg-wave/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wave focus-visible:ring-offset-2"
                >
                  Meet the Wave <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>
                <a
                  href="#roster"
                  className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full border border-wave text-wave font-semibold hover:bg-wave/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wave focus-visible:ring-offset-2"
                >
                  Back an athlete
                </a>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                Individual athlete support is separate from team sponsorship. It is a fan-support path for the athlete you choose. For commercial partnerships, appearances or brand opportunities,{" "}
                <Link to="/contact" className="text-wave underline underline-offset-2 hover:text-wave/80">
                  use Work with the athletes
                </Link>
                .
              </p>
            </div>

            <div className="lg:col-span-5">
              <blockquote className="rounded-3xl bg-muted/60 border-l-4 border-wave px-8 py-10 md:px-10 md:py-14 flex flex-col justify-center">
                <p className="serif text-2xl md:text-3xl leading-snug text-balance">
                  “A team. A community. A place to grow.”
                </p>
                <footer className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-wave">
                  Jersey Shore Wave
                </footer>
              </blockquote>
            </div>
            </div>
          </div>
        </section>

        {/* Roster */}
        <section id="roster" ref={rosterRef} className="py-12 md:py-16 scroll-mt-20">
          <div className={wrap}>
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-wave">The roster</span>
              <h2 className="text-3xl md:text-5xl mt-2 text-balance">Meet the Jersey Shore Wave</h2>
              <p className="text-muted-foreground mt-3">
                Every athlete here has a full profile, a share link, and a way to be backed.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-8">
              {loading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden bg-card shadow-card">
                      <div className="aspect-[3/4] bg-muted animate-pulse" />
                      <div className="p-3 space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                        <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))
                : cards.map((a) => {
                    const tagline = toPlainText(a.tagline ?? "");
                    const blurb = tagline || toExcerpt(a.bio ?? "", 90);
                    return (
                      <article
                        key={a.id}
                        className="rounded-2xl overflow-hidden bg-card shadow-card flex flex-col"
                      >
                        <div className="aspect-[3/4] overflow-hidden bg-muted">
                          <img
                            src={athleteImageUrl(a.image_path)}
                            alt={a.name}
                            loading="lazy"
                            width={768}
                            height={1024}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-3 flex flex-col gap-2 grow">
                          <div>
                            <h3 className="font-display text-base leading-tight">{a.name}</h3>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                              {a.sport}
                              {a.country ? ` · ${a.country}` : ""}
                            </p>
                            {blurb && <p className="serif text-sm text-primary mt-1">{blurb}</p>}
                          </div>
                          <div className="mt-auto grid gap-2">
                            <Link
                              to={`/athlete/${a.slug}?from=wave`}
                              onClick={() =>
                                trackEvent("profile_view", { campaign: CAMPAIGN, athlete: a.slug })
                              }
                              className="inline-flex items-center justify-center min-h-[44px] rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-wave hover:text-wave-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                              Meet {a.name.split(" ")[0]}
                            </Link>
                            <div
                              onClickCapture={() =>
                                trackEvent("share_click", { campaign: CAMPAIGN, athlete: a.slug })
                              }
                            >
                              <ShareStoryDialog
                                name={a.name}
                                slug={a.slug}
                                tagline={a.tagline}
                                bio={a.bio}
                                imageUrl={athleteImageUrl(a.image_path)}
                                className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-full border border-border bg-background text-sm font-semibold hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                              />
                            </div>
                            <Link
                              to={`/athlete/${a.slug}?from=wave#support`}
                              onClick={() =>
                                trackEvent("back_click", { campaign: CAMPAIGN, athlete: a.slug })
                              }
                              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-wave hover:text-wave-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                              <Heart className="w-4 h-4" aria-hidden /> Back her game
                            </Link>
                          </div>
                        </div>
                      </article>
                    );
                  })}
            </div>

            {!loading && cards.length === 0 && (
              <p className="text-muted-foreground py-10">
                The roster is not available right now. <Link to="/athletes" className="text-primary underline">See all athletes</Link>.
              </p>
            )}
          </div>
        </section>

        {/* Email signup */}
        <section className="py-12 md:py-16 bg-secondary text-secondary-foreground">
          <div className={wrap}>
            <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl text-balance">Get her name in your inbox</h2>
            <p className="mt-3 text-secondary-foreground/80">
              SIIS Weekly — new stories from the Wave roster and ways to back them.
            </p>
            <form onSubmit={onSubmitEmail} className="mt-6 flex flex-col sm:flex-row gap-3" noValidate>
              <label htmlFor="wave-email" className="sr-only">
                Email address
              </label>
              <input
                id="wave-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSignedUp(false);
                }}
                placeholder="you@example.com"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "wave-email-error" : undefined}
                className="flex-1 min-h-[44px] rounded-full px-5 bg-background text-foreground border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-wave hover:text-wave-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
              >
                <Mail className="w-4 h-4" aria-hidden /> Sign up
              </button>
            </form>
            {emailError && (
              <p id="wave-email-error" className="mt-3 text-sm text-highlight">
                {emailError}
              </p>
            )}
            <p role="status" aria-live="polite" className="mt-3 text-sm text-secondary-foreground/80">
              {signedUp ? "Thanks — finish confirming in the tab we just opened." : ""}
            </p>
            <p className="mt-2 text-sm text-secondary-foreground/70">
              SIIS Weekly only. Unsubscribe anytime.
            </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Wave;
